export const OUTBOUND_UTM_SOURCE = "pm_tld";

const configuredHostname = (() => {
  const domain = process.env.NEXT_PUBLIC_DOMAIN;
  if (!domain) return undefined;
  try {
    return new URL(domain).hostname?.toLowerCase();
  } catch {
    return undefined;
  }
})();

const normalize = (hostname?: string) => hostname?.toLowerCase();

const BARE_HOST_RE = /^[a-z0-9.-]+(?:\/|$|\?|#)/i;

export function getHostname(url: string): string | undefined {
  try {
    return normalize(new URL(url).hostname);
  } catch {
    if (url.startsWith("//")) {
      try {
        return normalize(new URL(`https:${url}`).hostname);
      } catch {
        return undefined;
      }
    }
    if (BARE_HOST_RE.test(url)) {
      try {
        return normalize(new URL(`https://${url}`).hostname);
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
}

export function isCrossDomainHref(url: string): boolean {
  const host = getHostname(url);
  if (!host) return false;
  if (!configuredHostname) return true;
  return host !== configuredHostname;
}

const REL_BASE = "http://__addutmsource_placeholder__";

/** Idempotent — adds utm_source=pm_tld, preserving the original URL shape. */
export function addUtmSource(url: string): string {
  if (!url) return url;
  if (url.startsWith("#")) return url;
  if (
    url.startsWith("tel:") ||
    url.startsWith("mailto:") ||
    url.startsWith("sms:")
  ) {
    return url;
  }

  const isProtocolRelative = url.startsWith("//");
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);
  const isBareHost =
    !hasScheme &&
    !isProtocolRelative &&
    !url.startsWith("/") &&
    BARE_HOST_RE.test(url);

  let parseInput = url;
  let usedRelBase = false;
  if (isProtocolRelative) parseInput = `https:${url}`;
  else if (isBareHost) parseInput = `https://${url}`;
  else if (!hasScheme) usedRelBase = true;

  try {
    const parsed = usedRelBase
      ? new URL(parseInput, REL_BASE)
      : new URL(parseInput);

    if (parsed.searchParams.has("utm_source")) return url;
    parsed.searchParams.set("utm_source", OUTBOUND_UTM_SOURCE);

    if (isProtocolRelative) {
      return `//${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    if (isBareHost) {
      return `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    if (usedRelBase) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Same as addUtmSource but only acts on cross-domain hrefs. */
export function addUtmIfOutbound(url: string): string {
  if (!url) return url;
  if (!isCrossDomainHref(url)) return url;
  return addUtmSource(url);
}
